import API from 'services/api';
import { displaySpinner, hideSpinner } from 'redux/actions/global';
import i18n from 'services/i18n';
import showErrorAlert, { parseError } from 'services/errorHandler';
import sessionService from 'services/session';
import { isFunction } from 'lodash-es';
import * as CONSTS from 'redux/constants';
import { getRouteWithToken, redirectToError, trackResetPinAction } from 'utilities';
import { sendGetPageFailMessage } from 'services/iframeService';
import { GA_CATEGORIES, HOSTED_PAGE_TYPES, INFO_ALERT_TIMEOUT, STATUSES } from 'consts';
import {
  APP_ROUTE_NOT_FOUND,
  CARD_ISSUING_ERROR,
  CARD_ISSUING_VIEW_PIN_THANK_YOU,
  THANK_YOU_PAGE_URL
} from 'routes/paths';
import history from 'shared/services/history';
import {
  RESET_ACTIVATE_PAGE_REDUX_STATE,
  RESET_PIN_FAIL,
  RESET_PIN_START,
  RESET_PIN_SUCCESS,
  RESET_RESET_PIN_REDUX_STATE
} from 'redux/constants/issuing/reset';
import setAlert, { AlertTypes } from 'utilities/toastUtil';
import {
  RESET_PIN_REDIRECTED_FAIL,
  RESET_PIN_REDIRECTED_START,
  RESET_PIN_REDIRECTED_SUCCESS
} from 'redux/constants';

const getResetPINPageStart = () => ({
  type: CONSTS.GET_RESET_PIN_PAGE_START
});

const getResetPINPageSuccess = data => ({
  type: CONSTS.GET_RESET_PIN_PAGE_SUCCESS,
  payload: { data }
});

const getResetPINPageFail = (error, data) => ({
  type: CONSTS.GET_RESET_PIN_PAGE_FAIL,
  payload: { error, data }
});

export const getResetPINPage = token => async dispatch => {
  const handleGetPageError = (error, url = '') => {
    const { status, data } = parseError(error);
    dispatch(getResetPINPageFail(status, data));
    redirectToError({ error, url });
  };

  if (!token) {
    sendGetPageFailMessage(HOSTED_PAGE_TYPES.cardIssuingResetPIN)();
    return redirectToError({ url: APP_ROUTE_NOT_FOUND });
  }

  try {
    dispatch(displaySpinner());
    dispatch(getResetPINPageStart());

    let response;
    try {
      response = await API.makeApiCall({
        method: 'get',
        url: `hosted/issuing/reset_pin/${token}`,
        pageToken: token
      });
    } catch (error) {
      const isRedirected = sessionService.handleUnauthorizedError(error, token);
      if (!isRedirected) {
        handleGetPageError(error, APP_ROUTE_NOT_FOUND);
      }
      return;
    }

    const { data: pageData } = response.data || {};
    dispatch(getResetPINPageSuccess(pageData));
    const { status } = pageData || {};
    if (
      status === STATUSES.done &&
      !window?.location?.pathname.includes(THANK_YOU_PAGE_URL)
    ) {
      history.push(getRouteWithToken(CARD_ISSUING_VIEW_PIN_THANK_YOU, token));
    }
  } catch (error) {
    handleGetPageError(error, CARD_ISSUING_ERROR);
  } finally {
    dispatch(hideSpinner({ meta: { pageType: HOSTED_PAGE_TYPES.cardIssuingResetPIN } }));
  }
};

const resetPINStart = () => ({
  type: RESET_PIN_START
});

const resetPINSuccess = data => ({
  type: RESET_PIN_SUCCESS,
  payload: { data }
});

const resetPINFail = error => ({
  type: RESET_PIN_FAIL,
  payload: { error }
});

export const resetReduxActivatePageState = () => ({
  type: RESET_ACTIVATE_PAGE_REDUX_STATE
});

export const resetPINIssuedCard =
  ({ token, pin, unAuthorizeCallback, dispatchToastSuccessMsg }) =>
  async dispatch => {
    try {
      dispatch(resetPINStart());
      const response = await API.makeApiCall({
        method: 'post',
        url: `hosted/issuing/${token}/reset_pin`,
        data: { pin },
        pageToken: token
      });

      const { data } = response.data || {};
      trackResetPinAction(GA_CATEGORIES.RESET_PIN_SUCCESS);
      dispatch(resetPINSuccess({ ...data, pin }));

      if (dispatchToastSuccessMsg) {
        setAlert({
          type: AlertTypes.INFO,
          text: i18n.t('activate_issued_card.reset_success'),
          timeout: INFO_ALERT_TIMEOUT
        });
      }
    } catch (error) {
      const { status } = parseError(error);
      const unAuthorized = sessionService.handleUnauthorizedError(error, token);
      if (unAuthorized) {
        dispatch(resetReduxActivatePageState());
        return isFunction(unAuthorizeCallback) && unAuthorizeCallback();
      }
      const getResetErr = err => {
        return i18n.exists(`activate_issued_card.${err}`)
          ? i18n.t(`activate_issued_card.${err}`)
          : i18n.t('activate_issued_card.unexpected_error');
      };
      showErrorAlert({
        error: getResetErr(status.error_code) || status.message,
        custom: true
      });
      trackResetPinAction(GA_CATEGORIES.RESET_PIN_FAILURE, status);
      dispatch(resetPINFail(getResetErr(status.error_code) || status.message));
    }
  };

const resetPINRedirectedStart = () => ({
  type: RESET_PIN_REDIRECTED_START
});

const resetPINRedirectedSuccess = data => ({
  type: RESET_PIN_REDIRECTED_SUCCESS,
  payload: { data }
});

const resetPINRedirectedFail = error => ({
  type: RESET_PIN_REDIRECTED_FAIL,
  payload: { error }
});

export const updateResetPinStateToRedirected = token => async dispatch => {
  try {
    dispatch(resetPINRedirectedStart());
    const response = await API.makeApiCall({
      method: 'post',
      url: 'hosted/issuing/pin/redirected_to_reset',
      data: { page_token: token }
    });

    const { data } = response.data || {};
    dispatch(resetPINRedirectedSuccess(data));
  } catch (error) {
    const { status } = parseError(error);
    const getResetErr = err => {
      return i18n.exists(`activate_issued_card.${err}`)
        ? i18n.t(`activate_issued_card.${err}`)
        : i18n.t('activate_issued_card.unexpected_error');
    };
    showErrorAlert({
      error: getResetErr(status.error_code) || status.message,
      custom: true
    });
    dispatch(resetPINRedirectedFail(getResetErr(status.error_code) || status.message));
  }
};

export const resetReduxResetPinState = () => ({
  type: RESET_RESET_PIN_REDUX_STATE
});
